import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
// hooks
import { useActions } from "../../../../common/hooks/useActions";
// actions
import { obbActionCreators } from "../obbModule";
// helpers
import { getUpdatedArray } from "../../../../common/utils/helpers/getUpdatedArray";
import { createInitialObbPassenger } from "../createInitialObbPassenger";
import { OBB_PASSENGER_TYPES } from "../obbPassengerTypes";
import type { ObbPassengerTypeCode } from "../obbPassengerTypes";
// components
import {
  PassengerAddButton,
  PassengerColumnHeaders,
  PassengerFormShell,
  PassengerNameFields,
  PassengerRemoveButton,
  PassengerRowMobileDivider
} from "../../shared/passengers";

export default function ObbPassengersForm() {
  const { t } = useTranslation();

  const passengerList = useSelector((state: any) => state.obb.passengerList);
  const updatePassengers = useActions(obbActionCreators?.updatePassengersAction, []);
  const resetPrebookDataInfo = useActions(obbActionCreators?.resetPrebookDataInfoAction, []);

  const handlePassengerFirstNameChange = (event: any, index: number) => {
    const updatedList = getUpdatedArray(passengerList, index, "first_name", event.target.value);
    updatePassengers(updatedList);
  };

  const handlePassengerLastNameChange = (event: any, index: number) => {
    const updatedList = getUpdatedArray(passengerList, index, "last_name", event.target.value);
    updatePassengers(updatedList);
  };

  const handlePassengerTypeChange = (index: number, passengerType: ObbPassengerTypeCode) => {
    const updatedList = getUpdatedArray(passengerList, index, "passenger_type", passengerType);
    updatePassengers(updatedList);
    resetPrebookDataInfo();
  };

  const removePassenger = (index: number) => {
    const updatedList = passengerList?.filter((passenger: any, i: number) => i !== index);
    updatePassengers(updatedList);
    resetPrebookDataInfo();
  };

  const addPassengerToList = () => {
    updatePassengers([...passengerList, createInitialObbPassenger(true)]);
  };

  return (
    <PassengerFormShell
      title={`${t("transport.passengers")} *`}
      columnHeaders={
        <PassengerColumnHeaders>
          <Grid item xs={3} className="passengers-subtitle" pb={1}>
            {t("transport.formFirstName")}
          </Grid>
          <Grid item xs={3} className="passengers-subtitle" pb={1}>
            {t("transport.formLastName")}
          </Grid>
          <Grid item xs={3} className="passengers-subtitle" pb={1}>
            {t("transport.formPassengerType")}
          </Grid>
        </PassengerColumnHeaders>
      }
      addButton={<PassengerAddButton onClick={addPassengerToList} />}
    >
      {passengerList?.length > 0 &&
        passengerList?.map((passenger: any) => {
          let index = passengerList.indexOf(passenger);
          return (
            <Grid container columnSpacing={{ xs: 1 }} mb={2} key={index}>
              <PassengerNameFields
                firstName={passengerList[index]?.first_name}
                lastName={passengerList[index]?.last_name}
                onFirstNameChange={(event: any) => {
                  handlePassengerFirstNameChange(event, index);
                }}
                onLastNameChange={(event: any) => {
                  handlePassengerLastNameChange(event, index);
                }}
              />
              <Grid item md={3} sm={12} xs={12} mb={{ xs: 2, md: 0 }}>
                <Select
                  name="passenger_type"
                  sx={{
                    width: "100%",
                    height: "43.5px",
                    borderRadius: 0,
                    fontFamily: "Neutraface2Book",
                    fontSize: "15px",
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "0.75px solid #575757 !important"
                    }
                  }}
                  value={passengerList[index]?.passenger_type}
                  onChange={event => handlePassengerTypeChange(index, event.target.value as ObbPassengerTypeCode)}
                >
                  {OBB_PASSENGER_TYPES.map(type => (
                    <MenuItem key={type.code} value={type.code}>
                      {t(type.labelKey)}
                    </MenuItem>
                  ))}
                </Select>
                <Box sx={{ display: { sm: "flex", md: "none", fontSize: "11px" } }} mt={0.5} ml={0.5}>
                  {t("transport.formPassengerType")}
                </Box>
              </Grid>
              <Grid item md={3} sm={12} xs={12} sx={{ display: "flex" }}>
                <Grid
                  item
                  xs={12}
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: { sm: "flex-end", md: "flex-start" }
                  }}
                >
                  <Grid item ml={{ xs: 0, sm: 3 }}>
                    {passenger?.hasRemove && <PassengerRemoveButton onClick={() => removePassenger(index)} />}
                  </Grid>
                </Grid>
              </Grid>
              <PassengerRowMobileDivider />
            </Grid>
          );
        })}
    </PassengerFormShell>
  );
}
